import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { api } from "../lib/api";
import { invalidateSettings } from "../lib/settings";
import { ArrowLeft, Save, Loader2, Users } from "lucide-react";

export default function SiteSettings() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [partners, setPartners] = useState([]);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("social");
  const [allPartners, setAllPartners] = useState([]);
  const [spotCfg, setSpotCfg] = useState({ mode: "off", featured_user_id: null });
  const [spotOptins, setSpotOptins] = useState([]);
  const [spotPreview, setSpotPreview] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user || user.role !== "admin") { navigate("/admin/login"); return; }
    Promise.all([
      api.get("/settings"),
      api.get("/admin/partners"),
      api.get("/admin/partners/opt-ins"),
      api.get("/admin/partners/spotlight/preview"),
    ]).then(([s, p, oi, pv]) => {
      setDoc(s.data); setAllPartners(p.data); setSpotOptins(oi.data); setSpotPreview(pv.data);
      setSpotCfg(s.data.partner_spotlight || { mode: "off" });
    });
  }, [user, loading, navigate]);

  if (loading || !user || !doc) return <div className="pt-32 text-center text-white/55 bg-[#081328] min-h-screen">Lade…</div>;

  const setSocial = (key, value) => setDoc({ ...doc, social: { ...doc.social, [key]: value } });

  const setPartner = (idx, key, value) => {
    const next = [...doc.partners];
    next[idx] = { ...next[idx], [key]: value };
    setDoc({ ...doc, partners: next });
  };

  const setMaterial = (idx, key, value) => {
    const next = [...doc.marketing_materials];
    next[idx] = { ...next[idx], [key]: value };
    setDoc({ ...doc, marketing_materials: next });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/admin/settings", {
        social: doc.social,
        partners: doc.partners,
        marketing_materials: doc.marketing_materials,
      });
      invalidateSettings();
      alert("Einstellungen gespeichert.");
    } catch (e) {
      alert(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };

  const setUserTier = async (uid, tier) => {
    await api.post("/admin/partners/set-tier", { user_id: uid, tier });
    const r = await api.get("/admin/partners");
    setAllPartners(r.data);
  };

  const saveSpotlight = async (cfg) => {
    const next = { ...spotCfg, ...cfg };
    // Don't try to save manual mode without a featured user — just update local state
    // and wait for the user to pick someone via the dropdown.
    if (next.mode === "manual" && !next.featured_user_id) {
      setSpotCfg(next);
      return;
    }
    setSpotCfg(next);
    try {
      await api.put("/admin/partners/spotlight", next);
      const pv = await api.get("/admin/partners/spotlight/preview");
      setSpotPreview(pv.data);
      invalidateSettings();
    } catch (e) {
      alert(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    }
  };

  return (
    <div className="min-h-screen bg-[#081328] text-[#F7F6F2] pt-24" data-testid="site-settings-page">
      <div className="container-sat max-w-5xl py-10">
        <div className="flex items-center justify-between mb-8">
          <Link to="/admin" className="inline-flex items-center gap-2 text-white/55 hover:text-[#D4AF37] text-sm"><ArrowLeft size={16} /> Zurück</Link>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-5 py-2 bg-[#D4AF37] text-[#081328] font-semibold rounded-full text-sm hover:bg-[#e3c252] disabled:opacity-50" data-testid="save-settings">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Speichern
          </button>
        </div>

        <div className="mb-10">
          <div className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-3">Site-Konfiguration</div>
          <h1 className="serif text-4xl md:text-5xl font-light tracking-tight">Social · Partner · Werbematerialien</h1>
        </div>

        <div className="flex gap-2 mb-8 border-b border-white/10">
          {[["social", "Social Media"], ["partners", "Partnerwelt"], ["materials", "Werbematerialien"], ["partner-tiers", "Partner-Stufen"], ["spotlight", "Spotlight"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`px-4 py-3 text-sm border-b-2 -mb-px ${tab === k ? "border-[#D4AF37] text-[#D4AF37]" : "border-transparent text-white/55 hover:text-white"}`}
              data-testid={`tab-${k}`}>{l}</button>
          ))}
        </div>

        {tab === "social" && (
          <div className="space-y-3" data-testid="social-editor">
            <p className="text-white/55 text-sm mb-6">Trage die vollständige URL ein. Leere Felder werden nicht angezeigt.</p>
            {["instagram", "youtube", "facebook", "linkedin", "tiktok", "spotify", "telegram", "whatsapp"].map((k) => (
              <div key={k} className="grid grid-cols-[140px,1fr] gap-4 items-center">
                <label className="text-sm capitalize text-white/65">{k}</label>
                <input type="url" value={doc.social[k] || ""} onChange={(e) => setSocial(k, e.target.value)}
                  placeholder={`https://… (${k})`} className="bg-white/[0.03] border border-white/10 rounded-lg px-4 py-2.5 text-sm focus:border-[#D4AF37] focus:outline-none"
                  data-testid={`social-input-${k}`} />
              </div>
            ))}
          </div>
        )}

        {tab === "partners" && (
          <div className="space-y-6" data-testid="partners-editor">
            {doc.partners.map((p, idx) => (
              <div key={p.key} className="border border-white/10 rounded-xl p-5 bg-white/[0.02]">
                <div className="text-[10px] uppercase tracking-wider mb-3" style={{ color: p.color }}>{p.key}</div>
                <div className="grid md:grid-cols-2 gap-3">
                  <FieldInput label="Name" value={p.name} onChange={(v) => setPartner(idx, "name", v)} />
                  <FieldInput label="CTA-Text" value={p.cta} onChange={(v) => setPartner(idx, "cta", v)} />
                  <FieldInput label="Logo-URL" value={p.logo} onChange={(v) => setPartner(idx, "logo", v)} />
                  <FieldInput label="URL" value={p.url} onChange={(v) => setPartner(idx, "url", v)} />
                </div>
                <FieldTextarea label="Beschreibung" value={p.description} onChange={(v) => setPartner(idx, "description", v)} />
              </div>
            ))}
          </div>
        )}

        {tab === "materials" && (
          <div className="space-y-3" data-testid="materials-editor">
            {doc.marketing_materials.map((m, idx) => (
              <div key={m.id} className="border border-white/10 rounded-xl p-4 bg-white/[0.02] grid md:grid-cols-[180px,1fr,140px,80px] gap-3 items-center">
                <div className="text-sm text-white/70">{m.label}</div>
                <input type="url" value={m.url || ""} onChange={(e) => setMaterial(idx, "url", e.target.value)} placeholder="Download-URL"
                  className="bg-white/[0.03] border border-white/10 rounded px-3 py-2 text-xs" data-testid={`material-url-${m.id}`} />
                <select value={m.type} onChange={(e) => setMaterial(idx, "type", e.target.value)} className="bg-[#081328] border border-white/10 rounded px-3 py-2 text-xs">
                  {["pdf", "zip", "doc", "img"].map((t) => <option key={t}>{t}</option>)}
                </select>
                <label className="text-xs text-white/65 inline-flex items-center gap-2">
                  <input type="checkbox" checked={!!m.available} onChange={(e) => setMaterial(idx, "available", e.target.checked)} />
                  aktiv
                </label>
              </div>
            ))}
          </div>
        )}

        {tab === "spotlight" && (
          <div data-testid="spotlight-editor" className="space-y-8">
            <div>
              <div className="text-white/55 text-sm mb-4">Modus festlegen — wer wird im Footer &amp; auf der Home gezeigt?</div>
              <div className="flex flex-wrap gap-3">
                {[["off", "Aus"], ["auto", "Automatisch (Top der letzten 30 Tage)"], ["manual", "Manuell festlegen"]].map(([k, l]) => (
                  <button key={k} onClick={() => saveSpotlight({ mode: k })}
                    className={`px-4 py-2.5 text-sm rounded-full border transition ${spotCfg.mode === k ? "bg-[#D4AF37] text-[#081328] border-[#D4AF37]" : "border-white/15 text-white/65 hover:border-[#D4AF37]/40"}`}
                    data-testid={`spotlight-mode-${k}`}>{l}</button>
                ))}
              </div>
            </div>

            {spotCfg.mode === "manual" && (
              <div>
                <div className="text-[10px] uppercase tracking-wider text-white/45 mb-2">Featured Partner wählen (nur Opt-In Partner)</div>
                {!spotCfg.featured_user_id && (
                  <div className="text-xs text-[#D4AF37] mb-3" data-testid="spotlight-pick-notice">
                    → Wähle unten einen Partner aus, um den Manuell-Modus zu aktivieren.
                  </div>
                )}
                {spotOptins.length === 0 ? (
                  <div className="text-white/45 text-sm border border-white/10 rounded-lg p-4">Noch hat sich kein Partner für das Spotlight angemeldet.</div>
                ) : (
                  <select value={spotCfg.featured_user_id || ""}
                          onChange={(e) => saveSpotlight({ mode: "manual", featured_user_id: e.target.value })}
                          className="bg-[#081328] border border-white/10 rounded px-3 py-2 text-sm w-full max-w-md"
                          data-testid="spotlight-featured-select">
                    <option value="">— wählen —</option>
                    {spotOptins.map((u) => (
                      <option key={u.id} value={u.id}>{u.spotlight_name || u.name} · {u.tier}</option>
                    ))}
                  </select>
                )}
              </div>
            )}

            <div>
              <div className="text-[10px] uppercase tracking-wider text-white/45 mb-2">Live-Vorschau</div>
              {spotPreview ? (
                <div className="border border-[#D4AF37]/30 rounded-xl p-5 bg-[#0E1A30]" data-testid="spotlight-preview">
                  <div className="serif text-2xl text-[#D4AF37]">{spotPreview.name}</div>
                  <div className="text-xs text-white/55 mt-1">{spotPreview.tier_label} · {spotPreview.helped_count} begleitet · CHF {Math.round(spotPreview.total_chf)}+</div>
                  {spotPreview.quote && <p className="text-white/75 italic text-sm mt-3">&bdquo;{spotPreview.quote}&ldquo;</p>}
                </div>
              ) : (
                <div className="text-white/40 text-sm border border-white/10 rounded-lg p-4 italic">Kein Spotlight aktuell sichtbar.</div>
              )}
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-wider text-white/45 mb-2">Aktuelle Opt-Ins ({spotOptins.length})</div>
              <div className="space-y-2">
                {spotOptins.map((u) => (
                  <div key={u.id} className="flex items-center justify-between border border-white/10 rounded-lg p-3 text-sm">
                    <div>
                      <div className="text-white/85">{u.spotlight_name || u.name}</div>
                      <div className="text-xs text-white/45">{u.email} · {u.tier}</div>
                    </div>
                    {u.quote && <div className="text-xs text-white/55 italic max-w-md truncate">&bdquo;{u.quote}&ldquo;</div>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === "partner-tiers" && (
          <div data-testid="tiers-editor">
            <p className="text-white/55 text-sm mb-6 inline-flex items-center gap-2"><Users size={14} /> Stufenzuteilung für SAT Partner. Provision wird automatisch angepasst.</p>
            {allPartners.length === 0 && <div className="text-white/40 text-sm">Noch keine Partner aktiv.</div>}
            <div className="space-y-2">
              {allPartners.map((p) => (
                <div key={p.id} className="grid md:grid-cols-[1fr,200px,140px,140px] gap-3 items-center border border-white/10 rounded-lg p-4 bg-white/[0.02]">
                  <div>
                    <div className="text-sm">{p.name || p.email}</div>
                    <div className="text-xs text-white/45">{p.email} · {p.referral_code}</div>
                  </div>
                  <select value={p.partner_tier || "ambassador"} onChange={(e) => setUserTier(p.id, e.target.value)} className="bg-[#081328] border border-white/10 rounded px-3 py-2 text-sm" data-testid={`tier-select-${p.id}`}>
                    <option value="ambassador">SAT Ambassador (20%)</option>
                    <option value="mentor_partner">SAT Mentor Partner (25%)</option>
                    <option value="master_partner">SAT Master Partner (30%)</option>
                  </select>
                  <div className="text-xs text-white/45 text-right">CHF {p.total_earned_chf?.toFixed(2)}</div>
                  <div className="text-xs text-white/45 text-right">{p.commissions_count} Orders</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const FieldInput = ({ label, value, onChange }) => (
  <div>
    <label className="text-[10px] uppercase tracking-wider text-white/45">{label}</label>
    <input value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded px-3 py-2 text-sm focus:border-[#D4AF37] focus:outline-none" />
  </div>
);
const FieldTextarea = ({ label, value, onChange }) => (
  <div className="mt-3">
    <label className="text-[10px] uppercase tracking-wider text-white/45">{label}</label>
    <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={2} className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded px-3 py-2 text-sm focus:border-[#D4AF37] focus:outline-none" />
  </div>
);
